import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  activeReasons,
  filterRows,
  REASON_ACTION,
  REASON_GLYPH,
  REASON_LABEL,
  REASON_TONE,
  sortRows,
  summariseQueue,
  type QueueSort,
} from './reasons';
import { StudentWorkDrawer } from './StudentWorkDrawer';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  attentionApi,
  type AttentionQueue,
  type AttentionReason,
  type AttentionRow,
} from '@/lib/api/attention';
import { trainerApi } from '@/lib/api/trainer';
import { cn } from '@/lib/cn';

/**
 * The trainer's worklist: who needs them, why, and what to do about it.
 *
 * This replaced a coverage heatmap. A heatmap is a survey instrument — good at
 * "which topic did the class not get", useless at "who do I go and talk to",
 * because it answers with two thousand cells and asks the trainer to find the
 * dark ones. A list can be filtered, sorted, actioned and, crucially, emptied.
 */
export function AttentionQueueView({
  batchId,
  onRaise,
}: {
  batchId: string;
  /**
   * Trainer-only: send one case to the TPO. Absent on the org-facing render,
   * where raising would mean escalating to yourself.
   */
  onRaise?: (row: AttentionRow) => void;
}) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<AttentionReason | 'all'>('all');
  const [sort, setSort] = useState<QueueSort>('urgency');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [viewing, setViewing] = useState<{ id: string; name: string } | null>(null);
  const [composing, setComposing] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['attention', batchId],
    queryFn: async () => (await attentionApi.getQueue(batchId)).data,
  });

  // Read-only is whatever the server said it is. Taking it from a prop would
  // put the question "may this person act" in the caller's hands, and the
  // caller is the least reliable place for it.
  const readOnly = data?.read_only ?? false;

  const invalidate = () => {
    // The roster shows the same flags, so it must not keep showing a student
    // the queue has just released.
    qc.invalidateQueries({ queryKey: ['attention', batchId] });
    qc.invalidateQueries({ queryKey: ['attention', 'roster', batchId] });
  };

  const resolve = useMutation({
    mutationFn: (v: { studentId: string; reason: AttentionReason; name: string }) =>
      attentionApi.resolve(batchId, v.studentId, v.reason),
    onSuccess: (_res, v) => {
      invalidate();
      // Undo, not a confirmation. Without it the only way back from a mis-click
      // is waiting out the snooze — five days for an inactive student.
      toast.success(`${v.name} marked handled`, {
        action: {
          label: 'Undo',
          onClick: () => {
            attentionApi
              .unresolve(batchId, v.studentId, v.reason)
              .then(invalidate)
              .catch(() => toast.error('Could not undo'));
          },
        },
      });
    },
    onError: () => toast.error('Could not mark that handled'),
  });

  const refresh = useMutation({
    mutationFn: () => attentionApi.refreshSimilarity(batchId),
    onSuccess: (res) => {
      invalidate();
      qc.invalidateQueries({ queryKey: ['attention', 'topics', batchId] });
      const { pairs_found: pairs, problems_scanned: problems } = res.data;
      // Reports the scan even when it finds nothing, so "no matches" reads as
      // "we looked" rather than "it did not run".
      toast.success(
        pairs > 0
          ? `${pairs} similar ${pairs === 1 ? 'pair' : 'pairs'} across ${problems} problems`
          : `No similar work found across ${problems} problems`,
      );
    },
    onError: () => toast.error('Could not check for similar work'),
  });

  const rows = useMemo(
    () => sortRows(filterRows(data?.rows ?? [], filter), sort),
    [data?.rows, filter, sort],
  );

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        <span className="sr-only">Loading the queue…</span>
        <Skeleton className="h-9 w-72 rounded-full" />
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Card className="p-6 text-center">
        <p className="text-sm text-muted-foreground">This queue could not be loaded.</p>
        <Button variant="link" onClick={() => void refetch()} className="mt-1">
          Try again
        </Button>
      </Card>
    );
  }

  const reasons = activeReasons(data);
  const togglePick = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="space-y-3">
      <Chips
        queue={data}
        reasons={reasons}
        active={filter}
        onChange={(r) => {
          setFilter(r);
          setPicked(new Set());
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Label htmlFor="queue-sort" className="text-xs font-semibold">
          Sort
        </Label>
        <Select value={sort} onValueChange={(v) => setSort(v as QueueSort)}>
          <SelectTrigger id="queue-sort" className="h-9 w-[150px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="urgency">Most urgent</SelectItem>
            <SelectItem value="recent">Most recent</SelectItem>
            <SelectItem value="name">Name</SelectItem>
          </SelectContent>
        </Select>

        {/*
          Every control below writes something. A read-only viewer keeps the
          sort — looking is the whole point — and loses the rest. The API
          refuses them anyway; hiding them is so nobody discovers that by
          pressing a button and being told no.
        */}
        {!readOnly && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPicked(new Set(rows.map((r) => r.user_id)))}
              disabled={rows.length === 0}
            >
              Select all shown
            </Button>

            {/*
              Bulk messaging is the point of the checkboxes. "Message the fourteen
              inactive students" is one action a trainer will take; doing it
              fourteen times is why they would not.
            */}
            <Button size="sm" onClick={() => setComposing(true)} disabled={picked.size === 0}>
              Message selected ({picked.size})
            </Button>

            <span className="flex-1" />

            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              {refresh.isPending ? 'Checking…' : 'Check for copied work'}
            </Button>
          </>
        )}
      </div>

      {rows.length === 0 ? (
        <Card className="border-dashed px-4 py-10 text-center shadow-none">
          <p className="text-sm font-medium">
            {filter === 'all'
              ? 'Nothing outstanding today.'
              : 'Nothing outstanding under this filter.'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{summariseQueue(data)}</p>
        </Card>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <li key={row.user_id}>
              <Card className="flex items-start gap-3 p-3 shadow-none">
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-muted text-xs font-bold uppercase text-muted-foreground">
                  {row.name.charAt(0)}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold">{row.name}</span>
                    <Badge variant="outline" className={cn('gap-1 py-px', REASON_TONE[row.reason])}>
                      <span aria-hidden="true">{REASON_GLYPH[row.reason]}</span>
                      {REASON_LABEL[row.reason]}
                    </Badge>
                    {row.all_reasons.length > 1 && (
                      <span className="text-[10.5px] text-muted-foreground">
                        +{row.all_reasons.length - 1} more
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {row.evidence}
                  </p>
                  <p className="mt-0.5 text-[11px] font-medium text-muted-foreground/70">
                    {REASON_ACTION[row.reason]}
                  </p>
                </div>

                <div className="flex flex-none items-center gap-2">
                  {!readOnly && (
                    <Label
                      htmlFor={`pick-${row.user_id}`}
                      className="flex cursor-pointer items-center gap-1.5 text-[11px] font-normal text-muted-foreground"
                    >
                      <Checkbox
                        id={`pick-${row.user_id}`}
                        checked={picked.has(row.user_id)}
                        onCheckedChange={() => togglePick(row.user_id)}
                        aria-label={`Select ${row.name}`}
                      />
                      pick
                    </Label>
                  )}

                  {/*
                    Every row needs a way to check before acting. A name and a
                    flag with no way to look at the work is a dead end, and a
                    dead end is what makes an integrity signal dangerous.
                  */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setViewing({ id: row.user_id, name: row.name })}
                  >
                    Work
                  </Button>

                  {/*
                    Raising is a trainer's act, not an administrator's: it means
                    "I spoke to them and you should know". A TPO pressing it
                    would be escalating to themselves.
                  */}
                  {!readOnly && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          resolve.mutate({
                            studentId: row.user_id,
                            reason: row.reason,
                            name: row.name,
                          })
                        }
                        disabled={resolve.isPending}
                      >
                        Handled
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="px-2 text-xs"
                        onClick={() => onRaise?.(row)}
                      >
                        Raise to TPO
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {rows.length > 0 && (
        <p className="px-1 text-xs text-muted-foreground">{summariseQueue(data)}</p>
      )}

      <p className="px-1 text-[11px] leading-relaxed text-muted-foreground/70">
        Integrity reasons are prompts to go and ask, not conclusions. Pasting has innocent
        explanations — an earlier attempt, boilerplate, notes. Asking the student to talk through
        their work answers it in thirty seconds.
      </p>

      <HandledSection rows={data.handled_rows ?? []} />

      <StudentWorkDrawer batchId={batchId} student={viewing} onClose={() => setViewing(null)} />

      <BulkMessageDialog
        batchId={batchId}
        open={composing}
        recipients={rows
          .filter((r) => picked.has(r.user_id))
          .map((r) => ({ id: r.user_id, name: r.name }))}
        onDone={() => {
          setComposing(false);
          setPicked(new Set());
        }}
        onOpenChange={setComposing}
      />
    </div>
  );
}

/**
 * One message to several students.
 *
 * Sent one request per recipient because that is the endpoint that exists, and
 * partial failure is reported honestly rather than swallowed: a trainer told
 * "sent to 14" when four bounced will believe the next lie too.
 */
function BulkMessageDialog({
  batchId,
  open,
  recipients,
  onDone,
  onOpenChange,
}: {
  batchId: string;
  open: boolean;
  recipients: { id: string; name: string }[];
  onDone: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  const [text, setText] = useState('');

  const send = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        recipients.map((r) => trainerApi.notifyStudent(batchId, r.id, text)),
      );
      return results.filter((r) => r.status === 'rejected').length;
    },
    onSuccess: (failed) => {
      if (failed === 0) {
        toast.success(`Sent to ${recipients.length} student${recipients.length === 1 ? '' : 's'}`);
      } else {
        toast.error(`Sent to ${recipients.length - failed}; ${failed} could not be reached`);
      }
      setText('');
      onDone();
    },
    onError: () => toast.error('Could not send'),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Message {recipients.length} student
            {recipients.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription className="truncate">
            {recipients.map((r) => r.name).join(', ')}
          </DialogDescription>
        </DialogHeader>

        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          autoFocus
          placeholder="Everyone gets the same message."
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => send.mutate()} disabled={!text.trim() || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Chips({
  queue,
  reasons,
  active,
  onChange,
}: {
  queue: AttentionQueue;
  reasons: AttentionReason[];
  active: AttentionReason | 'all';
  onChange: (r: AttentionReason | 'all') => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant={active === 'all' ? 'default' : 'outline'}
        size="sm"
        aria-pressed={active === 'all'}
        onClick={() => onChange('all')}
        className="rounded-full"
      >
        All
        <Count n={queue.flagged} on={active === 'all'} />
      </Button>

      {reasons.map((r) => {
        const on = active === r;
        const over = queue.overflow[r] ?? 0;
        return (
          <Button
            key={r}
            variant={on ? 'default' : 'outline'}
            size="sm"
            aria-pressed={on}
            onClick={() => onChange(r)}
            className="rounded-full"
          >
            <span aria-hidden="true">{REASON_GLYPH[r]}</span>
            {REASON_LABEL[r]}
            {/* The true total, including rows the per-reason cap trimmed. */}
            <Count n={queue.counts[r] ?? 0} on={on} />
            {/*
              Stated on the chip, not hidden in a tooltip. A trainer who cannot
              see that ten rows were withheld will read the list as the whole
              picture, which it is not.
            */}
            {over > 0 && (
              <span
                className={cn(
                  'text-[10px] font-medium',
                  on ? 'text-primary-foreground/70' : 'text-muted-foreground',
                )}
              >
                +{over}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}

function Count({ n, on }: { n: number; on: boolean }) {
  return (
    <span
      className={cn(
        'rounded-full px-1.5 text-[10px] font-bold tabular-nums',
        on ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground',
      )}
    >
      {n}
    </span>
  );
}

/**
 * What the trainer already dealt with, and why.
 *
 * The single most important thing on the screen for anyone who was not the one
 * who dealt with it. Without this an administrator opening the queue sees a
 * shortlist of students with flags against their names; with it they see mostly
 * closed items and the reason each was closed, which is a record of work done
 * rather than a list of suspects.
 *
 * Collapsed by default, because for the trainer it is history — they were
 * there. It is the reader who wasn't who opens it.
 */
function HandledSection({ rows }: { rows: AttentionRow[] }) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;

  return (
    <Card className="shadow-none">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs font-semibold hover:bg-accent/40"
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden="true"
        />
        Already dealt with
        <span className="tabular-nums font-normal text-muted-foreground">{rows.length}</span>
        {/*
          The reassuring reading, stated rather than left to be inferred: most
          flags turn out to be nothing, and that is the system working.
        */}
        <span className="ml-auto font-normal text-muted-foreground">
          most turn out to be nothing
        </span>
      </button>

      {open && (
        <ul className="divide-y border-t">
          {rows.map((row) => (
            <li key={`${row.user_id}-${row.reason}`} className="px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-semibold">{row.name}</span>
                <Badge variant="outline" className={cn('gap-1 py-px', REASON_TONE[row.reason])}>
                  <span aria-hidden="true">{REASON_GLYPH[row.reason]}</span>
                  {REASON_LABEL[row.reason]}
                </Badge>
                <span className="ml-auto text-[10.5px] text-muted-foreground">
                  {row.handled?.resolved_by} · {formatDay(row.handled?.resolved_at)}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">{row.evidence}</p>
              {row.handled?.note ? (
                <p className="mt-1 border-l-2 border-border pl-2 text-xs leading-relaxed">
                  “{row.handled.note}”
                </p>
              ) : (
                /*
                  A resolution with no note is still a resolution — the trainer
                  clicked Handled and moved on. Saying so is more honest than an
                  empty space that reads as a missing explanation.
                */
                <p className="mt-1 text-[11px] italic text-muted-foreground/70">
                  Marked handled, no note left.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function formatDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
}
